// src/pages/product/ProductEditPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';

/**
 * Product edit page.
 */
export const ProductEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useProduct();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    description: '',
    tagline: '',
    owner: '',
    legal_entity: '',
    legal_entity_id: '',
    status: '',
    is_builtin: false,
    is_public: false,
    capabilities: '',
    resource_types: '',
    icon_url: '',
    logo_url: '',
    color_primary: '',
    color_secondary: '',
    web_url: '',
    api_base_url: '',
    docs_url: '',
    support_url: '',
    oauth2_client_id: '',
    allowed_scopes: '',
    redirect_uris: '',
    allowed_grant_types: '',
    metadata: '',
    published_at: '',
  });

  useEffect(() => {
    if (id) {
      store.GetProduct(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);
  useEffect(() => {
    if (store.currentItem) {
      setFormData({
        slug: (store.currentItem as any).slug ?? '',
        name: (store.currentItem as any).name ?? '',
        description: (store.currentItem as any).description ?? '',
        tagline: (store.currentItem as any).tagline ?? '',
        owner: (store.currentItem as any).owner ?? '',
        legal_entity: (store.currentItem as any).legal_entity ?? '',
        legal_entity_id: (store.currentItem as any).legal_entity_id ?? '',
        status: (store.currentItem as any).status ?? '',
        is_builtin: (store.currentItem as any).is_builtin ?? false,
        is_public: (store.currentItem as any).is_public ?? false,
        capabilities: (store.currentItem as any).capabilities ?? '',
        resource_types: (store.currentItem as any).resource_types ?? '',
        icon_url: (store.currentItem as any).icon_url ?? '',
        logo_url: (store.currentItem as any).logo_url ?? '',
        color_primary: (store.currentItem as any).color_primary ?? '',
        color_secondary: (store.currentItem as any).color_secondary ?? '',
        web_url: (store.currentItem as any).web_url ?? '',
        api_base_url: (store.currentItem as any).api_base_url ?? '',
        docs_url: (store.currentItem as any).docs_url ?? '',
        support_url: (store.currentItem as any).support_url ?? '',
        oauth2_client_id: (store.currentItem as any).oauth2_client_id ?? '',
        allowed_scopes: (store.currentItem as any).allowed_scopes ?? '',
        redirect_uris: (store.currentItem as any).redirect_uris ?? '',
        allowed_grant_types: (store.currentItem as any).allowed_grant_types ?? '',
        metadata: (store.currentItem as any).metadata ?? '',
        published_at: (store.currentItem as any).published_at ?? '',
      });
    }
  }, [store.currentItem]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);

    try {
      await store.UpdateProduct(id, formData as any);
      navigate(`/products/${id}`);
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/products" className="text-muted-foreground hover:text-foreground">
            {t('product.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.edit')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('product.edit')}</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('product.details')}</CardTitle>
            <CardDescription>{t('product.updateInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Slug"
              type="text"
              value={formData.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              placeholder="Slug"
              required
            />
            <Input
              label="Name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Name"
              required
            />
            <Input
              label="Description"
              type="text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Description"
              required
            />
            <Input
              label="Tagline"
              type="text"
              value={formData.tagline}
              onChange={(e) => handleChange('tagline', e.target.value)}
              placeholder="Tagline"
              required
            />
            <Input
              label="Owner"
              type="text"
              value={formData.owner}
              onChange={(e) => handleChange('owner', e.target.value)}
              placeholder="Owner"
              required
            />
            <Input
              label="Legal Entity"
              type="text"
              value={formData.legal_entity}
              onChange={(e) => handleChange('legal_entity', e.target.value)}
              placeholder="Legal Entity"
              required
            />
            <Input
              label="Legal Entity Id"
              type="text"
              value={formData.legal_entity_id}
              onChange={(e) => handleChange('legal_entity_id', e.target.value)}
              placeholder="Legal Entity Id"
              required
            />
            <Input
              label="Status"
              type="text"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              placeholder="Status"
              required
            />
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.is_builtin}
                onChange={(e) => handleChange('is_builtin', e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <span className="text-sm font-medium text-foreground">Is Builtin</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) => handleChange('is_public', e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <span className="text-sm font-medium text-foreground">Is Public</span>
            </label>
            <Input
              label="Capabilities"
              type="text"
              value={formData.capabilities}
              onChange={(e) => handleChange('capabilities', e.target.value)}
              placeholder="Capabilities"
              required
            />
            <Input
              label="Resource Types"
              type="text"
              value={formData.resource_types}
              onChange={(e) => handleChange('resource_types', e.target.value)}
              placeholder="Resource Types"
              required
            />
            <Input
              label="Icon Url"
              type="url"
              value={formData.icon_url}
              onChange={(e) => handleChange('icon_url', e.target.value)}
              placeholder="Icon Url"
              required
            />
            <Input
              label="Logo Url"
              type="url"
              value={formData.logo_url}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              placeholder="Logo Url"
              required
            />
            <Input
              label="Color Primary"
              type="text"
              value={formData.color_primary}
              onChange={(e) => handleChange('color_primary', e.target.value)}
              placeholder="Color Primary"
              required
            />
            <Input
              label="Color Secondary"
              type="text"
              value={formData.color_secondary}
              onChange={(e) => handleChange('color_secondary', e.target.value)}
              placeholder="Color Secondary"
              required
            />
            <Input
              label="Web Url"
              type="url"
              value={formData.web_url}
              onChange={(e) => handleChange('web_url', e.target.value)}
              placeholder="Web Url"
              required
            />
            <Input
              label="Api Base Url"
              type="url"
              value={formData.api_base_url}
              onChange={(e) => handleChange('api_base_url', e.target.value)}
              placeholder="Api Base Url"
              required
            />
            <Input
              label="Docs Url"
              type="url"
              value={formData.docs_url}
              onChange={(e) => handleChange('docs_url', e.target.value)}
              placeholder="Docs Url"
              required
            />
            <Input
              label="Support Url"
              type="url"
              value={formData.support_url}
              onChange={(e) => handleChange('support_url', e.target.value)}
              placeholder="Support Url"
              required
            />
            <Input
              label="Oauth2 Client Id"
              type="text"
              value={formData.oauth2_client_id}
              onChange={(e) => handleChange('oauth2_client_id', e.target.value)}
              placeholder="Oauth2 Client Id"
              required
            />
            <Input
              label="Allowed Scopes"
              type="text"
              value={formData.allowed_scopes}
              onChange={(e) => handleChange('allowed_scopes', e.target.value)}
              placeholder="Allowed Scopes"
              required
            />
            <Input
              label="Redirect Uris"
              type="text"
              value={formData.redirect_uris}
              onChange={(e) => handleChange('redirect_uris', e.target.value)}
              placeholder="Redirect Uris"
              required
            />
            <Input
              label="Allowed Grant Types"
              type="text"
              value={formData.allowed_grant_types}
              onChange={(e) => handleChange('allowed_grant_types', e.target.value)}
              placeholder="Allowed Grant Types"
              required
            />
            <Input
              label="Metadata"
              type="text"
              value={formData.metadata}
              onChange={(e) => handleChange('metadata', e.target.value)}
              placeholder="Metadata"
              required
            />
            <Input
              label="Published At"
              type="text"
              value={formData.published_at}
              onChange={(e) => handleChange('published_at', e.target.value)}
              placeholder="Published At"
            />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to={`/products/${id}`}>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default ProductEditPage;
